import { Checkbox, Form, FormSection, RadioButton, Text } from "@centricsoftware/design-system";
import { FIELD_PERMISSIONS_OPTIONS_EDITABLE_ANYWHERE, FIELD_PERMISSIONS_OPTIONS_EDITABLE_FORM_ONLY, FIELD_PERMISSIONS_OPTIONS_EDITABLE_READ_ONLY, REMEMBER_LAST_VALUE_OPTIONS_NONE, REMEMBER_LAST_VALUE_OPTIONS_WHILE_CREATING } from "c8-platform";
import { observer } from "mobx-react-lite";
import React, { FC } from "react";
import panelCss from "./FormAttributesPanel.module.css";
import { useFormDefinitionStore } from "./FormDefinitionStore";

const ConfigurationEditView: FC = observer(() => {
	const {
		activeField,
		editPermissionsRadioValue,
		isAlwaysRememberLastValueCheckboxChecked,
		isAlwaysRememberLastValueCheckboxDisabled,
		isCreateOnlyCheckboxChecked,
		isEditPermissionsEditableOnCreateFormOnlyDisabled,
		loc: { attributeResource, resource },
		onRequiredCheckboxChange,
		onEditPermissionsChange,
		onCreateOnlyCheckboxChange,
		onPersistValueRadioChange,
		onPersistValueAlwaysCheckboxChange,
		rememberLastValueRadioValue,
	} = useFormDefinitionStore();

	const requiredConfigurableAttributeText = attributeResource("ConfigurableAttribute", "Required");
	const formEditPermissionsConfigurableAttributeText = attributeResource("ConfigurableAttribute", "FormEditPermissions");
	const editableAnywhereConfigurableAttributeText = attributeResource("ConfigurableAttribute", "EditableAnywhere");
	const formOnlyConfigurableAttributeText = attributeResource("ConfigurableAttribute", "FormOnly");
	const createOnlyConfigurableAttributeText = attributeResource("ConfigurableValue", "CreateOnly");
	const readOnlyText = resource("ReadOnly");
	const formRememberLastValueConfigurableAttributeText = attributeResource("ConfigurableAttribute", "FormRememberLastValue");
	const formDisplayedAsInformativeBoxConfigurableAttributeText = attributeResource("ConfigurableAttribute", "FormDisplayedAsInformativeBox");
	const formDontRememberLastValueConfigurableAttributeText = attributeResource("ConfigurableAttribute", "FormDontRememberLastValue");
	const formAlwaysRememberLastValueConfigurableAttributeText = attributeResource("ConfigurableAttribute", "FormAlwaysRememberLastValue");

	const { transientInfo } = activeField;

	return (
		<Form className={panelCss.formContainer}>
			<Checkbox
				aria-label={requiredConfigurableAttributeText}
				checked={transientInfo.required}
				label={requiredConfigurableAttributeText}
				onChange={onRequiredCheckboxChange}
			/>
			<FormSection title={formEditPermissionsConfigurableAttributeText} />
			<RadioButton
				aria-label={formEditPermissionsConfigurableAttributeText}
				onChange={onEditPermissionsChange}
				options={[
					{
						id: FIELD_PERMISSIONS_OPTIONS_EDITABLE_ANYWHERE,
						label: editableAnywhereConfigurableAttributeText,
						value: FIELD_PERMISSIONS_OPTIONS_EDITABLE_ANYWHERE,
					},
					{
						id: FIELD_PERMISSIONS_OPTIONS_EDITABLE_FORM_ONLY,
						label: formOnlyConfigurableAttributeText,
						value: FIELD_PERMISSIONS_OPTIONS_EDITABLE_FORM_ONLY,
						children: (
							<Checkbox
								aria-label={createOnlyConfigurableAttributeText}
								checked={isCreateOnlyCheckboxChecked}
								disabled={isEditPermissionsEditableOnCreateFormOnlyDisabled}
								label={createOnlyConfigurableAttributeText}
								onChange={onCreateOnlyCheckboxChange}
							/>
						),
					},
					{
						id: FIELD_PERMISSIONS_OPTIONS_EDITABLE_READ_ONLY,
						label: readOnlyText,
						value: FIELD_PERMISSIONS_OPTIONS_EDITABLE_READ_ONLY,
						children: (
							<Text color="subtler" dataCsiAutomation="FormDisplayedAsInformativeBox">
								{formDisplayedAsInformativeBoxConfigurableAttributeText}
							</Text>
						),
					},
				]}
				value={editPermissionsRadioValue}
			/>
			<FormSection title={formRememberLastValueConfigurableAttributeText} />
			<RadioButton
				aria-label={formRememberLastValueConfigurableAttributeText}
				onChange={onPersistValueRadioChange}
				options={[
					{
						id: REMEMBER_LAST_VALUE_OPTIONS_NONE,
						label: formDontRememberLastValueConfigurableAttributeText,
						value: REMEMBER_LAST_VALUE_OPTIONS_NONE,
					},
					{
						id: REMEMBER_LAST_VALUE_OPTIONS_WHILE_CREATING,
						label: formRememberLastValueConfigurableAttributeText,
						value: REMEMBER_LAST_VALUE_OPTIONS_WHILE_CREATING,
						children: (
							<Checkbox
								aria-label={formAlwaysRememberLastValueConfigurableAttributeText}
								checked={isAlwaysRememberLastValueCheckboxChecked}
								disabled={isAlwaysRememberLastValueCheckboxDisabled}
								label={formAlwaysRememberLastValueConfigurableAttributeText}
								onChange={onPersistValueAlwaysCheckboxChange}
							/>
						),
					},
				]}
				value={rememberLastValueRadioValue}
			/>
		</Form>
	);
});

export default ConfigurationEditView;
